import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import numberToWords from 'number-to-words';
import { useTranslation } from 'react-i18next';
import { format, isValid, parseISO } from 'date-fns';

import FormInput from '../../components/form/FormInput';
import FormSelect from '../../components/form/FormSelect';
import FormTextarea from '../../components/form/FormTextarea';
import FormDateInput from '../../components/form/FormDateInput';
import FormNumberInput from '../../components/form/FormNumberInput';

import { subDispositions, subSubDispositions } from '../../store/slices/callingSlice';
import { CRM_FIELD_ROW_COLS } from '../../utils/crmLayout';

// Submit format per field type. A `date` field sends the day only; `datetime` keeps the
// clock time the agent picked. Types not listed here submit their value untouched.
const SUBMIT_DATE_FORMATS = {
  date: 'yyyy-MM-dd',
  datetime: 'yyyy-MM-dd HH:mm:ss'
};

const Dispositions = ({ data = [], onUpdate }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [formValues, setFormValues] = React.useState({});
  const [dispositionsState, setDispositionsState] = React.useState({
    disposition: '',
    subDisposition: '',
    subDispositionName: '',
    subSubDisposition: '',
    subSubDispositionName: '',
    subDispositionOptions: [],
    subSubDispositionOptions: [],
    fields: []
  });

  // useEffect(() => {
  //   if (data && data.length > 0) {
  //     const defaultDisposition = data[0].DisposCode; // Set default disposition to the first option
  //     setDispositionsState((prevState) => ({
  //       ...prevState,
  //       disposition: defaultDisposition
  //     }));

  //     // Fetch sub-dispositions for the default disposition
  //     fetchDispositions('sub', findDispoID(defaultDisposition));
  //   }
  // }, [data]); // This useEffect will run once when `data` is available

  useEffect(() => {
    if (onUpdate) {
      const formattedFields = dispositionsState?.fields.map((field) => {
        const value = formValues[field.field_rank];
        let formattedValue = value;

        // Keyed on `type`, which is what AddEditDispositionModal saves and what
        // renderField below switches on. This used to read `field_type` — the CRM
        // fields' key, never present on a disposition field — so it never formatted
        // anything and date values went out as raw Date objects.
        const submitFormat = SUBMIT_DATE_FORMATS[field.type];
        if (submitFormat && value) {
          const parsedDate = typeof value === 'string' ? parseISO(value) : new Date(value);
          if (isValid(parsedDate)) {
            formattedValue = format(parsedDate, submitFormat);
          }
        }

        return {
          field_rank: field.field_rank,
          disposition_crm_value: formattedValue
        };
      });
      onUpdate({
        dispositioncode: dispositionsState?.disposition || undefined,
        subdispositioncode: dispositionsState?.subDispositionName || undefined,
        subsubdispositioncode: dispositionsState?.subSubDispositionName || undefined,
        fields: formattedFields || undefined
      });
    }
  }, [
    dispositionsState?.disposition,
    dispositionsState?.subDispositionName,
    dispositionsState?.subSubDispositionName,
    dispositionsState?.fields,
    formValues
  ]);

  const findDispoID = (value) => {
    const dispositionId = data?.find((item) => item.DisposCode === value);
    return dispositionId?.id;
  };

  const fetchDispositions = async (type, value) => {
    try {
      const payload = { disposition_id: value };
      const response =
        type === 'sub'
          ? await dispatch(subDispositions(payload)).unwrap()
          : await dispatch(subSubDispositions(payload)).unwrap();

      if (response && response.data && response.data.length > 0) {
        const options = response.data.map((item) => ({
          label: item.SubDisposCode,
          value: type === 'sub' ? item.sub_dispostion_id : item.sub_sub_dispostion_id
        }));
        setDispositionsState((prevState) => ({
          ...prevState,
          [`${type}DispositionOptions`]: options,
          [`${type}Disposition`]: ''
        }));
      } else {
        setDispositionsState((prevState) => ({
          ...prevState,
          [`${type}DispositionOptions`]: [],
          [`${type}Disposition`]: ''
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${type} dispositions:`, error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const findFields = data?.find((x) => x.DisposCode === value);
    setDispositionsState((prevState) => ({
      ...prevState,
      [name]: value,
      fields: [...(findFields?.fields || [])],
      subDisposition: '',
      subDispositionName: '',
      subSubDisposition: '',
      subSubDispositionName: ''
    }));
    setFormValues({});
    if (name === 'disposition') {
      fetchDispositions('sub', findDispoID(value));
    }
  };

  const handleDispositionsChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => {
      const updatedValues = { ...prevState, [name]: value || '' };
      return updatedValues;
    });
  };

  const handleSubDispositionChange = (e) => {
    const { value, label } = e.target;
    setDispositionsState((prevState) => ({
      ...prevState,
      subDisposition: value,
      subDispositionName: label,
      subSubDisposition: '',
      subSubDispositionName: ''
    }));
    fetchDispositions('subSub', value);
  };

  const getDispoOptions = () => {
    return data.map((item) => ({ label: item.DisposName, value: item.DisposCode })) || [];
  };

  const renderField = (field) => {
    const { field_rank, type, option, edit, label } = field;
    const isDisabled = edit === 'false';

    switch (type) {
      case 'text': {
        return (
          <FormInput
            name={field_rank}
            value={formValues[field_rank]}
            label={label}
            disabled={isDisabled}
            onChange={handleDispositionsChange}
          />
        );
      }
      case 'date': {
        return (
          <FormDateInput
            name={field_rank}
            dateFormat="dd/MM/yyyy"
            value={formValues[field_rank]}
            label={label}
            disabled={isDisabled}
            onChange={handleDispositionsChange}
          />
        );
      }
      case 'datetime': {
        return (
          <FormDateInput
            name={field_rank}
            dateFormat="dd/MM/yyyy HH:mm"
            showTimeSelect={true}
            timeIntervals="5"
            value={formValues[field_rank]}
            label={label}
            disabled={isDisabled}
            onChange={handleDispositionsChange}
          />
        );
      }
      case 'select': {
        const options = (option || '')
          .split(',')
          .map((opt) => opt.trim())
          .filter(Boolean)
          .map((opt) => ({
            value: opt,
            label: opt
          }));
        return (
          <FormSelect
            name={field_rank}
            value={formValues[field_rank]}
            label={label}
            options={options}
            disabled={isDisabled}
            onChange={handleDispositionsChange}
          />
        );
      }
      case 'number': {
        const numberValue = formValues[field_rank];

        const numberInWords = useMemo(() => {
          if (numberValue !== null && numberValue !== undefined && numberValue !== '') {
            const num = Number(numberValue);
            return isNaN(num) ? '' : numberToWords.toWords(num);
          }
          return '';
        }, [numberValue]);

        return (
          <div>
            <FormNumberInput
              name={field_rank}
              value={numberValue}
              label={label}
              disabled={isDisabled}
              onChange={handleDispositionsChange}
            />
            {numberInWords && (
              <div
                className="crm-field-words"
                style={{ marginTop: '4px', fontSize: '0.875rem', color: '#666' }}>
                {numberInWords}
              </div>
            )}
          </div>
        );
      }
      case 'textarea': {
        return (
          <FormTextarea
            name={field_rank}
            value={formValues[field_rank]}
            label={label}
            disabled={isDisabled}
            onChange={handleDispositionsChange}
            rows={1}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <div className="fs-4 fw-bolder py-2">{t('dispositions')}</div>
      <Row xs="3">
        <FormSelect
          label={t('dispositions')}
          placeholder={t('selectDispositions')}
          name="disposition"
          value={dispositionsState.disposition}
          options={getDispoOptions()}
          onChange={handleChange}
        />
        <FormSelect
          label={t('subDispositions')}
          name="subDisposition"
          placeholder={t('selectSubDispositions')}
          value={dispositionsState.subDisposition}
          options={dispositionsState.subDispositionOptions}
          onChange={handleSubDispositionChange}
        />
        <FormSelect
          label={t('subSubDispositions')}
          name="subSubDisposition"
          placeholder={t('selectSubSubDispositions')}
          value={dispositionsState.subSubDisposition}
          options={dispositionsState.subSubDispositionOptions}
          onChange={(e) =>
            setDispositionsState((prevState) => ({
              ...prevState,
              subSubDisposition: e.target.value,
              subSubDispositionName: e.target.label
            }))
          }
        />
      </Row>
      {dispositionsState?.fields?.length > 0 ? (
        <React.Fragment>
          <div className="fs-4 fw-bolder py-2">{t('DispositionFields')}</div>
          <Row {...CRM_FIELD_ROW_COLS} className="w-100">
            {dispositionsState?.fields.map((field) => (
              <Col key={field.field_rank}>{renderField(field)}</Col>
            ))}
          </Row>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

Dispositions.propTypes = {
  data: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default Dispositions;
